import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { money, STATUS_TONE, isScopedFinanceUser } from "./financeShell";
import { ClientWorkspaceLink } from "@/components/shared";

const PAYMENT_METHOD_OPTIONS = [
  { value: "manual",            label: "Manual" },
  { value: "stripe",            label: "Stripe" },
  { value: "wise",              label: "Wise" },
  { value: "paypal",            label: "PayPal" },
  { value: "ach",               label: "ACH" },
  { value: "bank_transfer",     label: "Bank Transfer" },
  { value: "thai_bank_transfer",label: "Thai Bank Transfer" },
  { value: "wire_transfer",     label: "Wire Transfer" },
  { value: "cash",              label: "Cash" },
  { value: "check",             label: "Check" },
];

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-semibold text-slate-900">{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><Icons.X className="h-5 w-5" /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

const Field = ({ label, ...p }) => (
  <label className="block mb-3"><span className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</span>
    <input {...p} className="w-full mt-1 bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-200" /></label>
);

// ── Reminder Panel ─────────────────────────────────────────────────────────────────────
function ReminderPanel({ invoiceId, invoiceStatus, user }) {
  const [rs, setRs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [pauseReason, setPauseReason] = useState("");
  const [showPauseForm, setShowPauseForm] = useState(false);

  const TERMINAL = ["paid", "void", "voided", "written_off", "cancelled", "superseded"];
  const isTerminal = TERMINAL.includes((invoiceStatus || "").toLowerCase());

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/finance/invoices/${invoiceId}/reminders`)
      .then(r => setRs(r.data))
      .catch(() => setRs(null))
      .finally(() => setLoading(false));
  }, [invoiceId]);

  useEffect(() => { load(); }, [load]);

  const fmtDt = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
    catch { return iso; }
  };

  const canEdit = user?.role === "administrator" || user?.role === "partner" ||
    (user?.permissions || []).some(p => p === "*" || p === "finance.edit" || p === "finance.approve");

  const doEnable = async () => {
    setBusy(true);
    try {
      await api.post(`/finance/invoices/${invoiceId}/reminders/enable`);
      toast.success("Reminders enabled");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to enable reminders");
    } finally { setBusy(false); }
  };

  const doPause = async () => {
    if (!pauseReason.trim()) { toast.error("Please provide a reason"); return; }
    setBusy(true);
    try {
      await api.post(`/finance/invoices/${invoiceId}/reminders/pause`, { reason: pauseReason });
      toast.success("Reminders paused");
      setShowPauseForm(false);
      setPauseReason("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to pause reminders");
    } finally { setBusy(false); }
  };

  const statusBadge = () => {
    if (!rs || rs.reminder_enabled == null) return <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-slate-100 text-slate-500 rounded-full" data-testid="reminder-status-badge">Not enrolled</span>;
    if (isTerminal) return <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-slate-100 text-slate-500 rounded-full" data-testid="reminder-status-badge"><Icons.CheckCircle className="h-3 w-3" /> Completed</span>;
    if (rs.reminder_enabled === false) return <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-amber-50 text-amber-700 rounded-full" data-testid="reminder-status-badge"><Icons.PauseCircle className="h-3 w-3" /> Paused</span>;
    return <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-full" data-testid="reminder-status-badge"><Icons.Bell className="h-3 w-3" /> Active</span>;
  };

  if (loading) return (
    <div className="border border-slate-200 rounded-xl p-4 mt-4 animate-pulse bg-slate-50" data-testid="reminder-panel-loading">
      <div className="h-3 w-24 bg-slate-200 rounded mb-2" /><div className="h-3 w-40 bg-slate-200 rounded" />
    </div>
  );

  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden mt-4" data-testid="reminder-panel">
      <div className="bg-slate-50 px-4 py-3 border-b border-slate-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icons.Bell className="h-4 w-4 text-slate-500" />
          <span className="text-sm font-semibold text-slate-700">Payment Reminders</span>
        </div>
        {statusBadge()}
      </div>
      <div className="px-4 py-3 space-y-3">
        <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
          <div>
            <p className="text-xs text-slate-400 uppercase tracking-wide mb-0.5">Last Sent</p>
            <p className="text-slate-700 font-medium" data-testid="reminder-last-sent">{fmtDt(rs?.last_sent_at)}</p>
          </div>
          <div>
            <p className="text-xs text-slate-400 uppercase tracking-wide mb-0.5">Next Scheduled</p>
            <p className="text-slate-700 font-medium" data-testid="reminder-next-due">
              {(!isTerminal && rs?.reminder_enabled && rs?.next_due_at) ? fmtDt(rs.next_due_at) : "—"}
            </p>
          </div>
          <div>
            <p className="text-xs text-slate-400 uppercase tracking-wide mb-0.5">Reminders Sent</p>
            <p className="text-slate-700 font-medium" data-testid="reminder-attempt-count">{rs?.attempt_count ?? 0}</p>
          </div>
          {rs?.pause_reason && (
            <div className="col-span-2">
              <p className="text-xs text-slate-400 uppercase tracking-wide mb-0.5">Pause Reason</p>
              <p className="text-amber-700 text-sm" data-testid="reminder-pause-reason">{rs.pause_reason}</p>
            </div>
          )}
        </div>

        {/* History */}
        {(rs?.history || []).length > 0 && (
          <div>
            <p className="text-xs text-slate-400 uppercase tracking-wide mb-1.5">Recent History</p>
            <div className="space-y-1" data-testid="reminder-history-list">
              {[...(rs.history)].reverse().slice(0, 5).map((h, i) => (
                <div key={i} className="flex items-center justify-between text-xs py-1 px-2 bg-slate-50 rounded-lg" data-testid={`reminder-history-item-${i}`}>
                  <span className="text-slate-600">{fmtDt(h.sent_at)} — #{h.sequence}</span>
                  <span className={`px-1.5 py-0.5 rounded-full font-medium ${h.status === "sent" ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>{h.status}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Controls */}
        {canEdit && !isTerminal && (
          <div className="pt-1 border-t border-slate-100 flex items-center gap-2 flex-wrap">
            {(!rs?.reminder_enabled) && (
              <button onClick={doEnable} disabled={busy} data-testid="reminder-enable-btn"
                className="text-xs px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium disabled:opacity-60 transition-colors">
                {busy ? "Enabling…" : (rs?.last_sent_at ? "Resume Reminders" : "Enable Reminders")}
              </button>
            )}
            {rs?.reminder_enabled && !showPauseForm && (
              <button onClick={() => setShowPauseForm(true)} data-testid="reminder-pause-trigger"
                className="text-xs px-3 py-1.5 bg-amber-50 hover:bg-amber-100 text-amber-700 border border-amber-200 rounded-lg font-medium transition-colors">
                Pause Reminders
              </button>
            )}
            {showPauseForm && (
              <div className="w-full space-y-2 pt-1">
                <input
                  data-testid="reminder-pause-reason-input"
                  placeholder="Reason for pausing (required for audit)"
                  className="w-full text-xs border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-200"
                  value={pauseReason}
                  onChange={e => setPauseReason(e.target.value)}
                />
                <div className="flex gap-2">
                  <button onClick={doPause} disabled={busy || !pauseReason.trim()} data-testid="reminder-pause-confirm"
                    className="text-xs px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-medium disabled:opacity-60 transition-colors">
                    {busy ? "Pausing…" : "Confirm Pause"}
                  </button>
                  <button onClick={() => { setShowPauseForm(false); setPauseReason(""); }} data-testid="reminder-pause-cancel"
                    className="text-xs px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-lg font-medium transition-colors">
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
        {canEdit && !isTerminal && (
          <p className="text-[10px] text-slate-400 pt-0.5">
            Note: pausing reminders does not release any payment obligation on associated engagements.
          </p>
        )}
      </div>
    </div>
  );
}

export default function InvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  // Gate C: destructive billing operations are not available to scoped finance users
  const scopedOnly = isScopedFinanceUser(user);
  const [d, setD] = useState(null);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => { api.get(`/finance/invoices/${id}`).then((r) => setD(r.data)).catch(() => toast.error("Invoice not found")); }, [id]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); setModal(null); setForm({}); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
    finally { setBusy(false); }
  };

  const genLink = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/finance/invoices/${id}/payment-link`, { send_email: true });
      setForm({ link: r.data.url, emailed: r.data.emailed });
      setModal("paylink");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create payment link"); }
    finally { setBusy(false); }
  };

  const downloadReceipt = async (receiptId, receiptNumber) => {
    try {
      const res = await api.get(`/finance/receipts/${receiptId}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `${receiptNumber || "receipt"}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download the receipt."); }
  };

  const downloadInvoicePdf = async () => {
    try {
      const res = await api.get(`/finance/invoices/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${d?.invoice?.invoice_number || id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download the invoice PDF."); }
  };

  const downloadPaidInvoicePdf = async () => {
    try {
      const res = await api.get(`/finance/invoices/${id}/paid-pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${d?.invoice?.invoice_number || id}_PAID.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not download the Paid Invoice PDF."); }
  };

  const printInvoicePdf = async () => {
    try {
      const res = await api.get(`/finance/invoices/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const win = window.open(url, "_blank");
      if (win) win.onload = () => { win.focus(); win.print(); }
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch { toast.error("Could not open the invoice for printing."); }
  };

  if (!d) return <div className="p-6 flex justify-center py-20"><Icons.Loader2 className="h-6 w-6 animate-spin text-[#F5A623]" /></div>;
  const inv = d.invoice;
  const ccy = inv.currency;
  const isDraft = ["draft", "pending_approval"].includes(inv.status);
  const canPay = !["void", "draft", "paid", "written_off"].includes(inv.status);
  const pvi = inv.proposal_vs_invoice || {};
  const hasDiff = pvi.original_amount != null && Math.abs(pvi.difference || 0) > 0.001;

  return (
    <div className="p-6 max-w-[1100px] mx-auto space-y-5" data-testid="invoice-detail">
      <div className="flex items-center gap-3" data-testid="invoice-nav">
        <button onClick={() => navigate("/finance/invoices")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900" data-testid="invoice-back"><Icons.ArrowLeft className="h-4 w-4" />Back to invoices</button>
        {inv.client_id && (
          <button onClick={() => navigate(`/client-workspace/${inv.client_id}`)}
            className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-indigo-700 border border-slate-200 rounded-lg px-2.5 py-1.5 transition-colors"
            data-testid="invoice-open-workspace">
            <Icons.User className="h-3.5 w-3.5" />Client Workspace
          </button>
        )}
      </div>

      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2.5">
            <h1 className="text-2xl font-bold text-slate-900 font-mono">{inv.invoice_number}</h1>
            <span className={`text-xs px-2.5 py-1 rounded-full capitalize ${STATUS_TONE[inv.status] || "bg-slate-100"}`} data-testid="inv-status">{inv.status.replace(/_/g, " ")}</span>
            <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 capitalize">from {inv.source}</span>
          </div>
          <div className="mt-1">
            <ClientWorkspaceLink clientId={inv.client_id} clientName={d.client?.full_name} clientNumber={d.client?.client_number} testId="invoice-client-link" />
          </div>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="inv-actions">
          {isDraft && <button onClick={() => act(() => api.post(`/finance/invoices/${id}/issue`), "Invoice issued")} disabled={busy} data-testid="inv-issue-btn" className="text-sm bg-[#171B2E] hover:bg-[#222842] text-white rounded-lg px-3.5 py-2 flex items-center gap-1.5"><Icons.Send className="h-4 w-4 text-[#F5A623]" />Issue</button>}
          {["issued"].includes(inv.status) && <button onClick={() => act(() => api.post(`/finance/invoices/${id}/send`), "Marked sent")} disabled={busy} data-testid="inv-send-btn" className="text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-3.5 py-2 flex items-center gap-1.5"><Icons.Mail className="h-4 w-4" />Mark Sent</button>}
          {canPay && <button onClick={() => { setForm({ amount: inv.balance, method: "manual" }); setModal("payment"); }} data-testid="inv-record-payment-btn" className="text-sm bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg px-3.5 py-2 flex items-center gap-1.5"><Icons.Banknote className="h-4 w-4" />Record Payment</button>}
          {canPay && <button onClick={genLink} disabled={busy} data-testid="inv-payment-link-btn" className="text-sm bg-violet-600 hover:bg-violet-700 text-white rounded-lg px-3.5 py-2 flex items-center gap-1.5"><Icons.Link className="h-4 w-4" />Payment Link</button>}
          <button onClick={downloadInvoicePdf} data-testid="inv-download-pdf-btn" className="text-sm border border-slate-200 hover:bg-slate-50 text-slate-700 rounded-lg px-3.5 py-2 flex items-center gap-1.5"><Icons.Download className="h-4 w-4" />Download PDF</button>
          <button onClick={printInvoicePdf} data-testid="inv-print-pdf-btn" className="text-sm border border-slate-200 hover:bg-slate-50 text-slate-700 rounded-lg px-3.5 py-2 flex items-center gap-1.5"><Icons.Printer className="h-4 w-4" />Print</button>
          {inv.status === "paid" && inv.balance <= 0 && (
            <button onClick={downloadPaidInvoicePdf} data-testid="inv-download-paid-pdf-btn" className="text-sm bg-emerald-700 hover:bg-emerald-800 text-white rounded-lg px-3.5 py-2 flex items-center gap-1.5"><Icons.BadgeCheck className="h-4 w-4" />Paid Invoice</button>
          )}
          {!["void", "written_off", "refunded"].includes(inv.status) &&
            <div className="relative group">
              <button data-testid="inv-more-btn" className="text-sm border border-slate-200 hover:border-slate-300 text-slate-600 rounded-lg px-3 py-2 flex items-center gap-1"><Icons.MoreHorizontal className="h-4 w-4" /></button>
              <div className="absolute right-0 mt-1 w-48 bg-white border border-slate-200 rounded-lg shadow-lg py-1 hidden group-hover:block z-10">
                {canPay && !scopedOnly && <button onClick={() => { setForm({ amount: inv.balance }); setModal("chargecard"); }} data-testid="inv-charge-card-btn" className="w-full text-left px-3 py-2 text-sm text-slate-600 hover:bg-slate-50">Charge Saved Card</button>}
                {!isDraft && !scopedOnly && <button onClick={() => { setForm({}); setModal("adjustment"); }} data-testid="inv-adjustment-btn" className="w-full text-left px-3 py-2 text-sm text-slate-600 hover:bg-slate-50">Adjustment</button>}
                {!isDraft && <button onClick={() => { setForm({}); setModal("plan"); }} data-testid="inv-plan-btn" className="w-full text-left px-3 py-2 text-sm text-slate-600 hover:bg-slate-50">Payment Plan</button>}
                {(inv.amount_paid > 0) && !scopedOnly && <button onClick={() => {
                  const stripePayment = d.payments.find((p) => p.processor === "stripe" && p.payment_intent_id);
                  const settleCcy = stripePayment?.settlement_currency?.toUpperCase();
                  const isXC = settleCcy && settleCcy !== (inv.currency || "USD").toUpperCase() && stripePayment?.settlement_amount != null;
                  const settleAmt = isXC ? (stripePayment.settlement_amount || 0) : (stripePayment?.amount || inv.amount_paid);
                  const alreadyRefunded = stripePayment?.refunded_amount || 0;
                  setForm({ amount: Math.max(0, settleAmt - alreadyRefunded) });
                  setModal("refund");
                }} data-testid="inv-refund-btn" className="w-full text-left px-3 py-2 text-sm text-slate-600 hover:bg-slate-50">Refund</button>}
                {!scopedOnly && <button onClick={() => { setForm({}); setModal("writeoff"); }} data-testid="inv-writeoff-btn" className="w-full text-left px-3 py-2 text-sm text-amber-600 hover:bg-slate-50">Write Off</button>}
                {!scopedOnly && <button onClick={() => act(() => api.post(`/finance/invoices/${id}/void`), "Invoice voided")} data-testid="inv-void-btn" className="w-full text-left px-3 py-2 text-sm text-rose-600 hover:bg-slate-50">Void</button>}
              </div>
            </div>}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          {/* Overpayment exception banner — Resolution Required */}
          {inv.status === "overpayment" && (() => {
            const overpaidAmt = Math.abs(inv.balance ?? 0);
            return (
              <div className="flex items-start gap-3 bg-orange-50 border border-orange-300 rounded-xl px-5 py-4"
                data-testid="inv-overpayment-banner">
                <Icons.AlertTriangle className="h-5 w-5 text-orange-600 shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-orange-800">
                    Overpayment — Resolution Required
                  </p>
                  <p className="text-sm text-orange-700 mt-0.5">
                    This invoice received <strong>{money(overpaidAmt, inv.currency)}</strong> more
                    than the invoice balance. The ledger reflects the actual amount received.
                    Resolve the excess through a refund or client credit before closing.
                  </p>
                  <p className="text-xs text-orange-600 mt-1.5 font-medium">
                    Overpayment: {money(overpaidAmt, inv.currency)} · No automated resolution has been applied.
                  </p>
                </div>
              </div>
            );
          })()}
          {/* Line items */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="inv-line-items">
            <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
              <h2 className="font-semibold text-slate-800">Line Items</h2>
              {isDraft && <button onClick={() => { setForm({ item_type: "one_time_charge", quantity: 1 }); setModal("addline"); }} data-testid="inv-add-line-btn" className="text-sm text-amber-700 hover:underline flex items-center gap-1"><Icons.Plus className="h-4 w-4" />Add</button>}
            </div>
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-slate-400 border-b border-slate-100"><th className="px-5 py-2 font-medium">Item</th><th className="px-3 py-2 font-medium text-right">Qty</th><th className="px-3 py-2 font-medium text-right">Unit</th><th className="px-5 py-2 font-medium text-right">Amount</th></tr></thead>
              <tbody>
                {inv.line_items.map((li) => (
                  <tr key={li.id} className="border-b border-slate-50">
                    <td className="px-5 py-2.5"><div className="font-medium text-slate-800">{li.name}{li.post_proposal && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">Post-Estimate</span>}</div>{li.description && <div className="text-xs text-slate-400">{li.description}</div>}</td>
                    <td className="px-3 py-2.5 text-right text-slate-600">{li.quantity}</td>
                    <td className="px-3 py-2.5 text-right text-slate-600">{money(li.unit_price, ccy)}</td>
                    <td className="px-5 py-2.5 text-right font-medium text-slate-800">{money(li.amount, ccy)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="px-5 py-3 border-t border-slate-100 space-y-1 text-sm">
              <div className="flex justify-between text-slate-500"><span>Subtotal</span><span>{money(inv.subtotal, ccy)}</span></div>
              {inv.discount_total > 0 && <div className="flex justify-between text-slate-500"><span>Discount</span><span>−{money(inv.discount_total, ccy)}</span></div>}
              {inv.tax_total > 0 && <div className="flex justify-between text-slate-500"><span>Tax</span><span>{money(inv.tax_total, ccy)}</span></div>}
              <div className="flex justify-between font-bold text-slate-900 text-base pt-1"><span>Total</span><span>{money(inv.total, ccy)}</span></div>
              <div className="flex justify-between text-emerald-600"><span>Paid</span><span>{money(inv.amount_paid, ccy)}</span></div>
              <div className="flex justify-between font-semibold text-slate-900"><span>Balance Due</span><span>{money(inv.balance, ccy)}</span></div>
            </div>
          </div>

          {/* Estimate vs Invoice audit */}
          {pvi.original_amount != null && (
            <div className={`rounded-xl border shadow-sm p-5 ${hasDiff ? "border-amber-200 bg-amber-50/40" : "border-slate-200 bg-white"}`} data-testid="proposal-vs-invoice">
              <h2 className="font-semibold text-slate-800 flex items-center gap-2"><Icons.GitCompare className="h-4 w-4 text-amber-600" />Estimate vs Invoice Audit</h2>
              <div className="grid grid-cols-3 gap-3 mt-3 text-sm">
                <div><div className="text-xs text-slate-400">Original Estimate</div><div className="font-semibold text-slate-800">{money(pvi.original_amount, ccy)}</div></div>
                <div><div className="text-xs text-slate-400">Final Invoice</div><div className="font-semibold text-slate-800">{money(pvi.final_amount, ccy)}</div></div>
                <div><div className="text-xs text-slate-400">Difference</div><div className={`font-semibold ${hasDiff ? "text-amber-700" : "text-slate-500"}`}>{pvi.difference > 0 ? "+" : ""}{money(pvi.difference, ccy)}</div></div>
              </div>
              {(pvi.reasons || []).length > 0 && (
                <div className="mt-3 border-t border-amber-100 pt-3 space-y-1.5">
                  {pvi.reasons.map((r, idx) => (
                    <div key={idx} className="text-xs text-slate-600 flex items-start gap-2"><Icons.CornerDownRight className="h-3.5 w-3.5 text-amber-500 mt-0.5" /><span><b>{r.delta > 0 ? "+" : ""}{money(r.delta, ccy)}</b> · {r.reason} <span className="text-slate-400">— {r.by}</span></span></div>
                  ))}
                </div>
              )}
              <p className="text-xs text-slate-400 mt-3 italic">The original estimate remains immutable. All differences are tracked here.</p>
            </div>
          )}

          {/* Payment Reminders Panel */}
          {inv && (
            <ReminderPanel
              invoiceId={id}
              invoiceStatus={inv.status}
              user={user}
            />
          )}

          {/* Payments */}
          {d.payments.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="inv-payments">
              <div className="px-5 py-3 border-b border-slate-100"><h2 className="font-semibold text-slate-800">Payments</h2></div>
              {d.payments.map((p) => {
                const isXC = p.settlement_currency &&
                  p.settlement_currency.toUpperCase() !== (ccy || "USD").toUpperCase() &&
                  p.settlement_amount != null;
                const displayAmt = isXC && p.invoice_currency_amount != null
                  ? p.invoice_currency_amount
                  : p.amount;
                return (
                  <div key={p.id} className="flex items-start gap-3 px-5 py-3 border-b border-slate-50 last:border-0" data-testid={`payment-row-${p.id}`}>
                    {/* Left: date · method · reference (truncates long Stripe IDs) */}
                    <div className="min-w-0 flex-1 overflow-hidden">
                      <div className="text-sm text-slate-600 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                        <span>{(p.received_date || p.created_at || "").slice(0, 10)}</span>
                        <span className="capitalize text-slate-500">{(p.method || "").replace(/_/g, " ")}</span>
                      </div>
                      {p.reference_number && (
                        <p className="text-xs text-slate-400 truncate mt-0.5" title={p.reference_number}>
                          {p.reference_number}
                        </p>
                      )}
                    </div>
                    {/* Right: amount column — shrink-0 ensures it never gets squeezed out */}
                    <div className="shrink-0 flex items-start gap-3">
                      <div className="text-right min-w-[110px]">
                        <div className="text-sm font-semibold text-emerald-600 whitespace-nowrap" data-testid={`payment-invoice-amt-${p.id}`}>
                          {money(displayAmt, ccy)}
                        </div>
                        {isXC && (
                          <div className="text-[11px] text-slate-400 whitespace-nowrap mt-0.5 leading-tight" data-testid={`payment-settlement-${p.id}`}>
                            {p.settlement_currency?.toUpperCase()} {Number(p.settlement_amount).toFixed(2)} settled
                          </div>
                        )}
                      </div>
                      <div className="w-14 text-right pt-0.5">
                        {p.receipt_id
                          ? <button onClick={() => downloadReceipt(p.receipt_id, p.receipt_number)} data-testid={`inv-download-receipt-${p.receipt_id}`} className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-amber-700"><Icons.Download className="h-3.5 w-3.5" />Receipt</button>
                          : <span className="text-xs text-slate-300">—</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-5">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-3 text-sm" data-testid="inv-meta">
            <div className="flex justify-between"><span className="text-slate-400">Issue Date</span><span className="text-slate-700">{inv.issue_date ? inv.issue_date.slice(0, 10) : "—"}</span></div>
            <div className="flex justify-between items-center"><span className="text-slate-400">Due Date</span>
              {isDraft ? <input type="date" defaultValue={inv.due_date ? inv.due_date.slice(0, 10) : ""} onBlur={(e) => e.target.value && api.patch(`/finance/invoices/${id}`, { due_date: e.target.value }).then(load)} data-testid="inv-due-date" className="border border-slate-200 rounded px-2 py-1 text-xs" /> : <span className="text-slate-700">{inv.due_date ? inv.due_date.slice(0, 10) : "—"}</span>}</div>
            <div className="flex justify-between"><span className="text-slate-400">Payment Terms</span><span className="text-slate-700">{inv.payment_terms || "—"}</span></div>
            <div className="flex justify-between"><span className="text-slate-400">Payment Rule</span><span className="text-slate-700 capitalize text-right">{(inv.payment_rule || "—").replace(/_/g, " ")}</span></div>
            {d.proposal && <div className="flex justify-between"><span className="text-slate-400">Estimate</span><span className="text-slate-700 font-mono text-xs">{d.proposal.proposal_number}</span></div>}
          </div>

          {d.payment_plan && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5" data-testid="inv-payment-plan">
              <h2 className="font-semibold text-slate-800 mb-2 capitalize">{(d.payment_plan.plan_type || "").replace(/_/g, " ")} Plan</h2>
              <div className="space-y-1.5">
                {d.payment_plan.installments.map((it) => (
                  <div key={it.id} className="flex justify-between text-sm"><span className="text-slate-500">{it.label} <span className="text-xs text-slate-400">{it.due_date ? `· ${it.due_date.slice(0, 10)}` : ""}</span></span><span className="font-medium text-slate-700">{money(it.amount, ccy)}</span></div>
                ))}
              </div>
            </div>
          )}

          {inv.notes_client && <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5"><div className="text-xs text-slate-400 uppercase tracking-wide mb-1">Client Notes</div><p className="text-sm text-slate-600">{inv.notes_client}</p></div>}
        </div>
      </div>

      {/* MODALS */}
      {modal === "payment" && <Modal title="Record Payment" onClose={() => setModal(null)}>
        <Field label="Amount" type="number" value={form.amount ?? ""} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="pay-amount" />
        <label className="block mb-3"><span className="text-xs font-medium text-slate-500 uppercase tracking-wide">Method</span>
          <select value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })} data-testid="pay-method" className="w-full mt-1 bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm">
            {PAYMENT_METHOD_OPTIONS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}</select></label>
        <Field label="Reference #" value={form.reference_number ?? ""} onChange={(e) => setForm({ ...form, reference_number: e.target.value })} data-testid="pay-ref" />
        <p className="text-xs text-slate-400 mb-3">Note: payment processors are not connected. Payments are recorded manually.</p>
        <button onClick={() => act(() => api.post(`/finance/invoices/${id}/payments`, { amount: Number(form.amount), method: form.method, reference_number: form.reference_number }), "Payment recorded")} disabled={busy} data-testid="pay-submit" className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg py-2.5 font-medium disabled:opacity-60">Record Payment</button>
      </Modal>}

      {modal === "addline" && <Modal title="Add Line Item (Post-Estimate)" onClose={() => setModal(null)}>
        <Field label="Name" value={form.name ?? ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="line-name" />
        <Field label="Quantity" type="number" value={form.quantity ?? 1} onChange={(e) => setForm({ ...form, quantity: e.target.value })} data-testid="line-qty" />
        <Field label="Unit Price" type="number" value={form.unit_price ?? ""} onChange={(e) => setForm({ ...form, unit_price: e.target.value })} data-testid="line-price" />
        <Field label="Reason" value={form.reason ?? ""} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="line-reason" placeholder="e.g. additional forms required" />
        <button onClick={() => act(() => api.post(`/finance/invoices/${id}/line-items`, { item_type: "one_time_charge", name: form.name, quantity: Number(form.quantity || 1), unit_price: Number(form.unit_price || 0), reason: form.reason }), "Line item added")} disabled={busy || !form.name} data-testid="line-submit" className="w-full bg-[#171B2E] text-white rounded-lg py-2.5 font-medium disabled:opacity-60">Add Item</button>
      </Modal>}

      {modal === "adjustment" && <Modal title="Apply Adjustment" onClose={() => setModal(null)}>
        <Field label="Amount (+/−)" type="number" value={form.amount ?? ""} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="adj-amount" />
        <Field label="Reason" value={form.reason ?? ""} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="adj-reason" />
        <button onClick={() => act(() => api.post(`/finance/invoices/${id}/adjustment`, { amount: Number(form.amount), reason: form.reason }), "Adjustment applied")} disabled={busy || !form.reason} data-testid="adj-submit" className="w-full bg-[#171B2E] text-white rounded-lg py-2.5 font-medium disabled:opacity-60">Apply</button>
      </Modal>}

      {modal === "refund" && (() => {
        const stripePayment = d.payments.find((p) => p.processor === "stripe" && p.payment_intent_id);
        const ccy_upper = (inv.currency || "USD").toUpperCase();
        const settleCcy = stripePayment?.settlement_currency?.toUpperCase() || ccy_upper;
        const isXC = settleCcy !== ccy_upper && stripePayment?.settlement_amount != null;
        const settleAmt = isXC ? (stripePayment.settlement_amount || 0) : (stripePayment?.amount || inv.amount_paid || 0);
        const canonicalAmt = isXC ? (stripePayment.invoice_currency_amount || stripePayment.amount || 0) : settleAmt;
        const alreadyRefunded = stripePayment?.refunded_amount || 0;
        const remainingRefundable = Math.max(0, settleAmt - alreadyRefunded);
        const enteredAmt = Number(form.amount || 0);
        const overRefund = enteredAmt > remainingRefundable + 0.005;
        return (
          <Modal title="Issue Refund" onClose={() => setModal(null)}>
            {isXC ? (
              <div className="bg-slate-50 rounded-lg p-3 mb-4 space-y-1.5 text-sm" data-testid="refund-xc-context">
                <div className="flex justify-between">
                  <span className="text-slate-500">Original Invoice</span>
                  <span className="font-semibold text-slate-800" data-testid="refund-canonical-amt">{money(canonicalAmt, inv.currency)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Payment Received</span>
                  <span className="font-semibold text-emerald-700" data-testid="refund-settlement-amt">{settleCcy} {settleAmt.toFixed(2)}</span>
                </div>
                {alreadyRefunded > 0 && (
                  <div className="flex justify-between" data-testid="refund-already-refunded">
                    <span className="text-slate-500">Previously Refunded</span>
                    <span className="font-medium text-amber-700">{settleCcy} {alreadyRefunded.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between border-t border-slate-200 pt-1.5">
                  <span className="font-medium text-slate-700">Available to Refund</span>
                  <span className="font-bold text-slate-900" data-testid="refund-available">{settleCcy} {remainingRefundable.toFixed(2)}</span>
                </div>
              </div>
            ) : (
              <p className="text-xs text-slate-500 mb-3">Refunds on card payments are sent back to the client's card via Stripe. Partial amounts are allowed.</p>
            )}
            <Field label={isXC ? `Refund Amount (${settleCcy})` : "Amount"} type="number" value={form.amount ?? ""} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="refund-amount" />
            {overRefund && <p className="text-xs text-rose-600 mb-2" data-testid="refund-over-limit">Exceeds refundable amount ({settleCcy} {remainingRefundable.toFixed(2)})</p>}
            <Field label="Reason" value={form.reason ?? ""} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="refund-reason" />
            <button onClick={() => act(() => api.post(`/finance/invoices/${id}/refund`, { amount: Number(form.amount), reason: form.reason }), "Refund issued")} disabled={busy || !form.reason || !form.amount || overRefund} data-testid="refund-submit" className="w-full bg-amber-600 text-white rounded-lg py-2.5 font-medium disabled:opacity-60">Issue Refund</button>
            {isXC && <p className="text-[10px] text-slate-400 mt-2 text-center" data-testid="refund-currency-note">Amount entered in {settleCcy} (original settlement currency). Invoice balance updates accordingly.</p>}
          </Modal>
        );
      })()}

      {modal === "chargecard" && <Modal title="Charge Saved Card" onClose={() => setModal(null)}>
        <p className="text-xs text-slate-500 mb-3">Charges the client's card on file (saved on their first online payment). Ideal for deposits or installments.</p>
        <Field label="Amount" type="number" value={form.amount ?? ""} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="chargecard-amount" />
        <Field label="Note (optional)" value={form.note ?? ""} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="chargecard-note" />
        <button onClick={() => act(() => api.post(`/finance/invoices/${id}/charge-saved-card`, { amount: Number(form.amount), note: form.note }), "Card charged")} disabled={busy || !form.amount} data-testid="chargecard-submit" className="w-full bg-emerald-600 text-white rounded-lg py-2.5 font-medium disabled:opacity-60">Charge Card</button>
      </Modal>}

      {modal === "paylink" && <Modal title="Payment Link Created" onClose={() => setModal(null)}>
        <p className="text-sm text-slate-600 mb-3">{form.emailed ? "A secure payment link was emailed to the client." : "Share this secure Stripe payment link with the client:"}</p>
        <div className="flex items-center gap-2 mb-3">
          <input readOnly value={form.link || ""} data-testid="paylink-url" className="flex-1 bg-slate-50 border border-slate-300 rounded-lg px-3 py-2 text-xs text-slate-700" />
          <button onClick={() => { navigator.clipboard.writeText(form.link || ""); toast.success("Link copied"); }} data-testid="paylink-copy" className="text-sm bg-[#171B2E] text-white rounded-lg px-3 py-2">Copy</button>
        </div>
        <a href={form.link} target="_blank" rel="noreferrer" data-testid="paylink-open" className="block text-center text-sm text-violet-600 hover:underline">Open payment page →</a>
      </Modal>}


      {modal === "writeoff" && <Modal title="Write Off Invoice" onClose={() => setModal(null)}>
        <Field label="Reason" value={form.reason ?? ""} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="writeoff-reason" />
        <button onClick={() => act(() => api.post(`/finance/invoices/${id}/writeoff`, { reason: form.reason }), "Invoice written off")} disabled={busy || !form.reason} data-testid="writeoff-submit" className="w-full bg-amber-600 text-white rounded-lg py-2.5 font-medium disabled:opacity-60">Write Off</button>
      </Modal>}

      {modal === "plan" && <Modal title="Create Payment Plan" onClose={() => setModal(null)}>
        <p className="text-sm text-slate-500 mb-3">Split the balance of {money(inv.balance, ccy)} into installments.</p>
        <label className="block mb-3"><span className="text-xs font-medium text-slate-500 uppercase tracking-wide">Plan Type</span>
          <select value={form.plan_type || "50_50"} onChange={(e) => setForm({ ...form, plan_type: e.target.value })} data-testid="plan-type" className="w-full mt-1 bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm">
            <option value="deposit">Deposit</option><option value="50_50">50 / 50</option><option value="monthly">Monthly (3)</option></select></label>
        <button onClick={() => {
          const bal = inv.balance; const pt = form.plan_type || "50_50";
          let inst = pt === "deposit" ? [{ label: "Deposit", amount: Math.round(bal * 0.5 * 100) / 100 }, { label: "Balance", amount: Math.round(bal * 0.5 * 100) / 100 }]
            : pt === "monthly" ? [1, 2, 3].map((n) => ({ label: `Month ${n}`, amount: Math.round(bal / 3 * 100) / 100 }))
            : [{ label: "Installment 1", amount: Math.round(bal * 0.5 * 100) / 100 }, { label: "Installment 2", amount: Math.round(bal * 0.5 * 100) / 100 }];
          return act(() => api.post(`/finance/invoices/${id}/payment-plan`, { plan_type: pt, installments: inst }), "Payment plan created");
        }} disabled={busy} data-testid="plan-submit" className="w-full bg-[#171B2E] text-white rounded-lg py-2.5 font-medium disabled:opacity-60">Create Plan</button>
      </Modal>}
    </div>
  );
}
